import { ref, watch, computed } from '@vue/composition-api';
import usePersistedPerPage from '@/libs/utils/perPage';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import moment from 'moment';
import store from '@/store';

export default function usePathwayCohortStudentsList(cohortId) {
  // Use toast
  const toast = useToast();

  const refStudentsListTable = ref(null);
  const isCurrent = ref(true);

  const tableColumns = [
    // { key: 'multiSelect', label: '', stickyColumn: true, variant: 'primary', sortable: false },
    {
      key: 'multiSelect',
      label: '',
      tdClass: 'td-checkbox checkbox-self sticky-checkbox',
      thClass: 'td-checkbox checkbox-self sticky-checkbox',
      variant: 'primary',
      stickyColumn: true,
      sortable: false,
    },
    {
      key: 'name',
      sortable: true,
      stickyColumn: true,
      variant: 'primary',
      tdClass: 'td-checkbox sticky-primary',
      thClass: 'td-checkbox sticky-primary',
    },
    { key: 'status', sortable: true },
    { key: 'modality', sortable: true },
    { key: 'studentStatus', sortable: true },
    { key: 'compliance', sortable: true },
    { key: 'uploadedResume', label: 'Resume', sortable: true },
    { key: 'uploadedCoe', label: 'COE', sortable: true },
    { key: 'uploadedTranscript', label: 'Transcript', sortable: true },
    { key: 'submittedToVa', label: 'VA Submission', sortable: true },
    { key: 'fundingSource', label: 'Funding', sortable: true },
    { key: 'toc', label: 'ToC', sortable: true },
    { key: 'retake', label: 'Retake', sortable: true },
    { key: 'actions' },
  ];

  const perPage = usePersistedPerPage('pathwayCohortStudentsList', 10);
  const totalProfiles = ref(0);
  const currentPage = ref(1);
  const totalPageCount = ref(0);
  const perPageOptions = [3, 5, 10, 15, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('student.fullName');
  const isSortDirDesc = ref(false);

  const idsOnPage = ref([]);

  const visibilityFilter = ref(null);
  const statusFilter = ref(null);
  // { [columnKey]: string[] } from RosterColumnFilters, sent as comma-separated params.
  const columnFilters = ref({});

  const dataMeta = computed(() => {
    const localItemsCount = refStudentsListTable.value ? refStudentsListTable.value.localItems.length : 0;

    console.log(perPage.value, currentPage.value, localItemsCount, totalProfiles.value);

    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalProfiles.value,
    };
  });

  const refetchData = () => {
    if (refStudentsListTable.value) {
      refStudentsListTable.value.refresh();
    }
  };

  watch([currentPage, perPage, searchQuery, visibilityFilter, statusFilter, isCurrent], (a) => {
    refetchData();
  });

  watch(
    columnFilters,
    () => {
      if (currentPage.value !== 1) currentPage.value = 1;
      else refetchData();
    },
    { deep: true }
  );

  const fetchProfiles = (ctx, callback) => {
    const pw = cohortId;

    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    const pl = {
      search: searchQuery.value,
      limit: perPage.value,
      page: currentPage.value,
      sortBy: sb,

      populate: 'student,reassignFrom,statistics,compliance',
    };

    if (visibilityFilter.value) {
      pl.visibility = visibilityFilter.value;
    }
    Object.entries(columnFilters.value).forEach(([key, selected]) => {
      if (selected && selected.length > 0) pl[key] = selected.join(',');
    });

    if (statusFilter.value) {
      pl.status = statusFilter.value;
    }

    if (!isCurrent.value) {
      pl.reassignFrom = cohortId;
    }

    store
      .dispatch('rosterStoreModule/fetchPathwayCohortStudents', { cohortId, queryParams: pl })
      .then((response) => {
        const { results, totalResults, totalPages } = response.data;
        idsOnPage.value = results.map((result) => result.id || result._id);

        callback(results);
        totalProfiles.value = totalResults;
        totalPageCount.value = totalPages;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching student list-pending',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  return {
    fetchProfiles,
    tableColumns,
    perPage,
    currentPage,
    totalProfiles,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refStudentsListTable,
    idsOnPage,
    isCurrent,

    refetchData,

    // Extra Filters
    visibilityFilter,
    statusFilter,
    columnFilters,

    totalPageCount,
  };
}
